<template>
    <div v-if="!isDismissed" class="notification-background relative rounded-xl p-2 md:p-3 overflow-hidden mb-4 hidden md:block">
        <div class="notification-card bg-surface-raised rounded-xl p-3 md:p-4">
            <div class="flex items-start gap-2 md:gap-3">
                <div class="shrink-0">
                    <i class="bi bi-stars text-2xl" aria-hidden="true"></i>
                </div>
                <div class="flex-1">
                    <h3 class="nadia-chat-bold mb-2 md:mb-3">{{ t("components.teamEvaluations.title") }}</h3>
                    <p class="nadia-chat mb-3 text-pretty">{{ t("components.teamEvaluations.body") }}</p>
                </div>
                <button
                    v-if="isDismissible"
                    class="shrink-0 text-ink-muted hover:text-ink transition-colors"
                    :aria-label="t('components.teamEvaluations.close')"
                    @click="handleDismiss">
                    <i class="bi bi-x text-2xl" aria-hidden="true"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import backgroundImage from "~img/nadia_teams_eval_bg.jpg";
import { useFetch } from "~vue/api";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

defineProps({
    isDismissible: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(["dismiss"]);

const isDismissed = ref(false);

// Computed is needed for reactivity with v-bind() in scoped styles
const backgroundImageUrl = computed(() => `url(${backgroundImage})`);

const dismissPayload = ref(null);

const { execute: saveDismissal } = useFetch("/api/accounts/ui-interactions", { immediate: false }).post(dismissPayload).json();

// TODO: Replace with useUIInteractions composable (PR #4479 review)
// Future: Component could call composable's createInteraction() which would:
// 1. POST to API
// 2. Auto-refetch state
// 3. Remove need for local isDismissed ref and emit("dismiss")
// Example: const { createInteraction } = useUIInteractions()
//          await createInteraction('nadia_team_eval_notification_dismissed')
const handleDismiss = async () => {
    isDismissed.value = true;
    emit("dismiss");
    logUserInteraction("nadia_team_eval_notification_dismissed");

    dismissPayload.value = {
        interaction_type: "nadia_team_eval_notification_dismissed",
    };

    await saveDismissal();
    dismissPayload.value = null;
};
</script>

<style scoped>
.notification-background {
    background: v-bind(backgroundImageUrl) center / cover;
}

/* Tonal drops the legacy gradient-image frame: the inner card stands alone
   with a plain border. Purple stays only on the sparkle, as Nadia's voice. */
html[data-theme="tonal"] .notification-background {
    background: none;
    padding: 0;
}

html[data-theme="tonal"] .notification-card {
    border: 1px solid var(--color-border);
}
</style>
